import * as React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { CardComponentDataProps } from '../../cards/dividedCard/DividedCard';
import { ellipsify } from '../../../../typography/ellipsify';
import { useTheme } from '../../../../theming/useTheme';
import { MEDIA_BREAKPOINTS } from '../../../../mediaQueries.const';
import { Link } from '../../link/Link';
import { H4, semanticCSS } from '../../../../typography/semanticTags';
import { BaseUnit } from '../../../../theming/baseUnitDefinition';

const createStyles = (
  borderColor: string,
  linksColor: string,
  linkHoverColor: string,
  baseUnit: BaseUnit,
) => StyleSheet.create({
  sectionItem: {
    padding: `${baseUnit * 0.5}px 0`,
    margin: `0 ${baseUnit * 0.5}px`,
    /*
      width is calculation of 50% - margins. The list is 200% width
      therefore to fit the visual container we set the item width to 50%
     */
    width: `calc(50% - ${baseUnit}px)`,
    borderTop: `1px solid ${borderColor}`,
    flex: '0 0 auto',
    boxSizing: 'border-box',
    position: 'relative',
    ':first-child': {
      borderTop: 'none',
    },
    ':last-child': {
      [MEDIA_BREAKPOINTS.SMALL]: {
        display: 'none',
      },
    },
  },
  link: {
    textDecoration: 'none',
    boxSizing: 'border-box',
    color: linksColor,
    ':visited': {
      color: linksColor,
      textDecoration: 'none',
    },
    ':active': {
      color: linksColor,
      textDecoration: 'none',
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      ':hover': {
        color: linkHoverColor,
        textDecoration: 'none',
      },
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      ':hover': {
        color: 'initial',
      },
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      '-webkitLineClamp': '2',
      ':hover': {
        color: 'initial',
      },
    },
  },
});

export interface ListItemComponentThemeProps {
  borderColor: string
  linkColor: string
  linkHoverColor: string
  baseUnit: BaseUnit
}

const componentConfigurationKey = 'listItem';
export type ListItemProps = ListItemComponentThemeProps & CardComponentDataProps;

export const ListItemComponent: React.FunctionComponent<ListItemProps> = (
  {
    borderColor,
    linkColor,
    linkHoverColor,
    baseUnit,
    title,
    articleUrl,
    numberOfLines = 2,
  },
) => {
  const styles = createStyles(
    borderColor,
    linkColor,
    linkHoverColor,
    baseUnit,
  );
  // @ts-ignore TS2345
  const { style } = StyleSheet.create(ellipsify(numberOfLines));
  return (
    <Link
      href={articleUrl}
      className={css(
        styles.link,
        styles.sectionItem,
      )}
      key={articleUrl}
    >
      <H4 styles={semanticCSS(style)}>{title}</H4>
    </Link>
  );
};

const mapThemeProps = (theme: ListItemComponentThemeProps) => ({
  borderColor: theme.borderColor,
  linkColor: theme.linkColor,
  linkHoverColor: theme.linkHoverColor,
  baseUnit: theme.baseUnit,
});

export const ListItem = (props: CardComponentDataProps) => {
  const themeContext = useTheme(mapThemeProps, componentConfigurationKey);
  return <ListItemComponent {...themeContext} {...props} />;
};
